import { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { Siren, Shield, AlertTriangle, Cable, Network } from 'lucide-react'
import { clsx } from 'clsx'
import { formatDistanceToNow } from 'date-fns'
import { api } from '../lib/api'

interface OpenIncident {
  id: number
  title: string
  severity: string
  status: string
  started_at: string | null
  owner: string
  downstream_services: string[]
  downstream_count: number
  services: number[]
}

interface KevAdded {
  cve_id: string
  vendor: string
  product: string
  date_added: string | null
  ransomware: boolean
}

interface CableFault {
  title: string
  cables: string[]
  url: string | null
  published_at: string | null
}

interface WarRoomPayload {
  incidents: OpenIncident[]
  incidents_open: number
  incidents_sev1: number
  kev_added: KevAdded[]
  kev_count: number
  cable_faults: CableFault[]
}

const SEV_TONE: Record<string, string> = {
  sev1: 'bg-rose/20 text-rose border-rose/50',
  sev2: 'bg-orange/15 text-orange border-orange/40',
  sev3: 'bg-amber/15 text-amber-light border-amber/40',
  sev4: 'bg-blue/15 text-blue-light border-blue/40',
}

/** Composite "what's on fire right now" panel for /app/cyber-warroom.
 * Reads /api/war-room/active which joins open postmortems + KEV velocity +
 * cable faults + service-graph blast radius into a single payload. */
export default function ActiveWarRoom() {
  const [data, setData] = useState<WarRoomPayload | null>(null)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    let mounted = true
    const pull = () => {
      api.get('/api/war-room/active', { params: { hours: 48 } })
        .then(r => { if (mounted) setData(r.data) })
        .catch(() => { if (mounted) setData(null) })
        .finally(() => { if (mounted) setLoading(false) })
    }
    pull()
    const t = setInterval(pull, 60_000)
    return () => { mounted = false; clearInterval(t) }
  }, [])

  if (loading) return <div className="card p-4 text-text-muted text-xs">Loading situation-room state…</div>
  if (!data) return null

  const nothingActive = data.incidents_open === 0 && data.kev_count === 0 && data.cable_faults.length === 0
  if (nothingActive) {
    return (
      <div className="card p-4 border border-emerald/30 bg-emerald/5">
        <div className="flex items-center gap-2 text-emerald-light text-sm">
          <Shield size={16} />
          <span className="font-semibold">Situation Room — calm</span>
          <span className="text-text-muted text-[11px] font-normal">no open incidents · no KEV adds in 48h · no active cable faults</span>
        </div>
      </div>
    )
  }

  return (
    <div className="card p-4 border border-rose/30 bg-rose/[0.03]">
      <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
        <h2 className="text-sm font-semibold text-text-primary flex items-center gap-2">
          <Siren size={16} className="text-rose animate-pulse" /> Active Situation Room
          <span className="text-[10px] text-text-muted font-normal">last 48h · refreshes every 60s</span>
        </h2>
        <div className="flex items-center gap-2 text-[10px]">
          <span className={clsx('px-2 py-0.5 rounded border', data.incidents_sev1 > 0 ? 'bg-rose/20 text-rose border-rose/50' : 'bg-surface-raised text-text-muted border-border')}>
            {data.incidents_sev1} SEV1
          </span>
          <span className="px-2 py-0.5 rounded border bg-surface-raised text-text-muted border-border">
            {data.incidents_open} open
          </span>
          <span className="px-2 py-0.5 rounded border bg-rose/15 text-rose-light border-rose/40">
            {data.kev_count} new KEV
          </span>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-3">
        {/* Open incidents w/ blast radius */}
        <div className="space-y-1.5">
          <h3 className="text-[10px] uppercase tracking-wider text-text-muted font-semibold">Open incidents</h3>
          {data.incidents.length === 0 && <p className="text-[11px] text-text-muted italic">None.</p>}
          {data.incidents.slice(0, 6).map(i => (
            <Link key={i.id} to={`/app/postmortems`}
              className="block p-2 rounded border border-border hover:bg-surface-hover text-xs">
              <div className="flex items-center justify-between gap-2">
                <span className={clsx('px-1.5 py-0.5 rounded border text-[9px] font-mono uppercase', SEV_TONE[i.severity] || 'bg-surface-raised text-text-muted border-border')}>
                  {i.severity}
                </span>
                <span className="font-medium text-text-primary truncate flex-1">{i.title}</span>
              </div>
              {i.downstream_count > 0 && (
                <div className="text-[9px] text-text-muted mt-1 flex items-center gap-1" title={i.downstream_services.join(', ')}>
                  <Network size={9} /> {i.downstream_count} downstream service{i.downstream_count === 1 ? '' : 's'}
                </div>
              )}
              {i.started_at && (
                <div className="text-[9px] text-text-muted mt-0.5">
                  started {formatDistanceToNow(new Date(i.started_at), { addSuffix: true })}
                  {i.owner && <> · owner: {i.owner}</>}
                </div>
              )}
            </Link>
          ))}
        </div>

        {/* Recent KEV adds */}
        <div className="space-y-1.5">
          <h3 className="text-[10px] uppercase tracking-wider text-text-muted font-semibold">Just added to KEV</h3>
          {data.kev_added.length === 0 && <p className="text-[11px] text-text-muted italic">None in last 48h.</p>}
          {data.kev_added.slice(0, 6).map(k => (
            <Link key={k.cve_id} to={`/app/cve/${encodeURIComponent(k.cve_id)}`}
              className="block p-2 rounded border border-border hover:bg-surface-hover text-xs">
              <div className="flex items-center justify-between gap-2">
                <span className="font-mono text-[11px] text-blue-light">{k.cve_id}</span>
                {k.ransomware && <span className="badge-red text-[9px]">RW</span>}
              </div>
              <div className="text-[10px] text-text-muted truncate">
                {k.vendor} {k.product}
              </div>
              {k.date_added && (
                <div className="text-[9px] text-text-muted mt-0.5">
                  added {formatDistanceToNow(new Date(k.date_added), { addSuffix: true })}
                </div>
              )}
            </Link>
          ))}
        </div>

        {/* Cable faults */}
        <div className="space-y-1.5">
          <h3 className="text-[10px] uppercase tracking-wider text-text-muted font-semibold">Cable faults (7d)</h3>
          {data.cable_faults.length === 0 && <p className="text-[11px] text-text-muted italic">No reported faults.</p>}
          {data.cable_faults.slice(0, 6).map((c, i) => (
            <a key={i} href={c.url || '#'} target={c.url ? '_blank' : undefined} rel="noreferrer noopener"
              className="block p-2 rounded border border-border hover:bg-surface-hover text-xs">
              <div className="flex items-center gap-2 mb-0.5">
                <Cable size={11} className="text-cyan shrink-0" />
                <span className="text-text-primary text-[11px] line-clamp-1">{c.title}</span>
              </div>
              {c.cables.length > 0 && (
                <div className="text-[9px] text-text-muted">
                  {c.cables.slice(0, 3).join(', ')}{c.cables.length > 3 && ` +${c.cables.length - 3}`}
                </div>
              )}
            </a>
          ))}
        </div>
      </div>
    </div>
  )
}
